import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { useLargeReportConfirm } from "src/hooks/useLargeReportConfirm";
import { useReportRangeLimit } from "src/hooks/useReportRangeLimit";

import { ActionButton, CardLayout, SelectField } from "../v3";
import { AdminService, ProjectService, PurchaseService } from "../../services";
import { getFilteredOptions, selectAllOption, selectAllUsers } from "../../utils";
import { subcontractorReportValidation } from "../../pages/report/validation";
import type { ReportType, SubcontractorReportQuery } from "../../services/analytics.types";
import { ReportDateRangeFields } from "./ReportDateRangeFields.component";

/** A picker option; "all" is the synthetic select-all entry. */
interface PickerOption {
  id: number | "all";
  [key: string]: unknown;
}

interface SubcontractorReportFilters {
  subcontractorIds: number[] | null;
  projectIds: number[] | null;
  createdByIds: number[] | null;
  fromDate: string;
  toDate: string;
}

type FormErrors = Partial<Record<keyof SubcontractorReportFilters, string>>;

interface SubcontractorReportFormProps {
  reportType?: ReportType;
  handleClear?: () => void;
  onGenerate?: (payload: SubcontractorReportQuery, options: { onError: () => void }) => void;
}

const selectedIds = (options: PickerOption[]): number[] =>
  options.map((option) => option.id).filter((id): id is number => id !== "all");

export const SubcontractorReportForm = ({
  reportType = "summary",
  handleClear = () => {},
  onGenerate = () => {},
}: SubcontractorReportFormProps) => {
  const { months: maxRangeMonths } = useReportRangeLimit();
  const { confirmLargeRange, largeRangeDialog } = useLargeReportConfirm();
  const [selectedSubcontractors, setSelectedSubcontractors] = useState<PickerOption[]>([selectAllOption as PickerOption]);
  const [selectedProjects, setSelectedProjects] = useState<PickerOption[]>([selectAllOption as PickerOption]);
  const [selectedUsers, setSelectedUsers] = useState<PickerOption[]>([selectAllUsers as PickerOption]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [reportDetails, setReportDetails] = useState<SubcontractorReportFilters>({
    subcontractorIds: null,
    projectIds: null,
    createdByIds: null,
    fromDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    toDate: moment().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    handleClear();
    setLoading(false);
  }, [reportType]);

  const updateDetailsState = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoading(false);
    handleClear();
    const { name, value } = event.target;
    setReportDetails((prev) => ({ ...prev, [name]: value }));
    if (name === "fromDate" || name === "toDate") setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /** Shared multi-select handler: "All" clears the filter, otherwise the chosen ids are sent. */
  const handleMultiSelectChange =
    (
      field: "subcontractorIds" | "projectIds" | "createdByIds",
      setSelected: React.Dispatch<React.SetStateAction<PickerOption[]>>,
      allOption: PickerOption,
    ) =>
    (options: PickerOption[]) => {
      setLoading(false);
      handleClear();

      if (options.some((option) => option.id === "all")) {
        setSelected([allOption]);
        setReportDetails((prev) => ({ ...prev, [field]: null }));
      } else {
        setSelected(options);
        setReportDetails((prev) => ({ ...prev, [field]: selectedIds(options) }));
      }

      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

  const handleGenerateReport = async () => {
    setErrors({});
    const validationErrors: FormErrors = subcontractorReportValidation(
      reportDetails,
      selectedSubcontractors[0]?.id,
      selectedProjects[0]?.id,
      selectedUsers[0]?.id,
      maxRangeMonths,
    );
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!(await confirmLargeRange(reportDetails.fromDate, reportDetails.toDate))) return;

    try {
      setLoading(true);

      const payload: SubcontractorReportQuery = {
        ...reportDetails,
        fromDate: moment(reportDetails.fromDate).startOf("day").toISOString(),
        toDate: moment(reportDetails.toDate).endOf("day").toISOString(),
        reportType,
      };

      onGenerate(payload, { onError: () => setLoading(false) });
    } catch (error) {
      setLoading(false);
      toast.error("Failed to generate report");
      console.error("🚀 ~ handleGenerateReport ~ error:", error);
    }
  };

  return (
    <CardLayout>
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <SelectField
            label="Subcontractor"
            value={selectedSubcontractors}
            getData={PurchaseService.getVendors}
            getDataStaticFilters={{ type: "SUBCONTRACTOR", sortBy: "name", sortOrder: "asc" }}
            required
            multiSelect
            error={!!errors.subcontractorIds}
            helperText={errors.subcontractorIds}
            onChange={handleMultiSelectChange("subcontractorIds", setSelectedSubcontractors, selectAllOption as PickerOption)}
            optionFilter={(options) => getFilteredOptions(options, selectedSubcontractors, selectAllOption)}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <SelectField
            label="Project"
            value={selectedProjects}
            getData={ProjectService.getProjectList}
            getDataStaticFilters={{ sortBy: "name", sortOrder: "asc" }}
            required
            multiSelect
            error={!!errors.projectIds}
            helperText={errors.projectIds}
            onChange={handleMultiSelectChange("projectIds", setSelectedProjects, selectAllOption as PickerOption)}
            optionFilter={(options) => getFilteredOptions(options, selectedProjects, selectAllOption)}
            style={{ width: "100%" }}
          />
          <SelectField
            label="Entry By"
            value={selectedUsers}
            getData={AdminService.getUserList}
            getDataStaticFilters={{ type: "USER", sortBy: "firstName", sortOrder: "asc" }}
            optionValueFields={["firstName", "lastName"]}
            required
            multiSelect
            error={!!errors.createdByIds}
            helperText={errors.createdByIds}
            onChange={handleMultiSelectChange("createdByIds", setSelectedUsers, selectAllUsers as PickerOption)}
            optionFilter={(options) => getFilteredOptions(options, selectedUsers, selectAllUsers)}
            style={{ width: "100%" }}
          />
        </Box>
        <ReportDateRangeFields
          fromDate={reportDetails.fromDate}
          toDate={reportDetails.toDate}
          onChange={updateDetailsState}
          errors={errors}
        />
        {largeRangeDialog}
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <ActionButton buttonText="Generate" color="primary" disabled={loading} onButtonClick={handleGenerateReport} />
      </Box>
    </CardLayout>
  );
};
